import { Portal } from '@mui/material';
import { Button, Dropdown } from '@wot/components';
import { ICategoryType, ITrialClass } from '@wot/typings';
import { addWeeks, formatISO, parseJSON, startOfWeek } from 'date-fns';
import { formatInTimeZone, utcToZonedTime } from 'date-fns-tz';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { MdClose } from 'react-icons/md';
import { plansApi } from '../../api/plan';
import { AddPayload, sessionsApi } from '../../api/sessions';
import BottomSheet from '../../components/Bottomsheet';
import Calender from '../../components/Calender/Calender';
import CalenderProvider from '../../components/Calender/CalenderContext';
import { useAppSelector } from '../../hooks';
import xhr from '../../utils/fetch';
import { MdDeleteOutline } from 'react-icons/md';

// user all existing sessions in the calender
// tutor availablily (removing his booked sessions)
// on select slot, if more than one subject in the plan, show dropdown and add a subject to each slot
// get existing completed sessions
// When applying repeat every week, check for conflict and show some errors
// on hover the slot show date and time in tooltip

interface Props {
  planId: string;
  tutorId: string;
  userId: string;
  setBookSessionsPortal: any;
  categoryType: ICategoryType;
  category: {
    name: string[];
    grade?: string;
    board?: string;
    level?: string;
    type?: string;
  };
  remainingSessions: number;
  completedSessions: number;
  totalSessions: number;
}

interface CategorySlot {
  start: string;
  end: string;
  category: {
    name: string;
    grade?: string;
    board?: string;
    level?: string;
    type?: string;
  };
}

export default function BookSessions({
  setBookSessionsPortal,
  planId,
  tutorId,
  category,
  categoryType,
  remainingSessions,
  completedSessions,
  totalSessions
}: Props) {
  const [selectedSlots, setSelectedSlots] = useState<CategorySlot[]>([]);
  const [loading, setLoading] = useState(false);
  const { timezone } = useAppSelector((state) => state.preferencesSlice);
  const [, setToday] = useState(startOfWeek(new Date()));
  const [availableSlots, setAvailableSlots] = useState([]);
  const [showPrompt, setShowPrompt] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<CategorySlot>();
  const [filters, setFilters] = useState<string[]>([]);
  const [filteredBookedSlots, setFilteredBookedSlots] = useState<CategorySlot[]>([]);
  const [currentFilter, setCurrentFilter] = useState('');
  const { userData } = useAppSelector((state) => state.userSlice);
  const [classes, setClasses] = useState<ITrialClass[]>([]);

  useEffect(() => {
    if (userData?._id) {
      sessionsApi.get(userData._id).then((data) => {
        setClasses(data);
      })
    }
  }, []);

  useEffect(() => {
    xhr({
      url: `/t/${tutorId}/available-slots`,
      method: 'GET'
    })
      .then((res) => {
        if (res.status === 'OK') {
          setAvailableSlots(res.data);
        }
      })
      .catch((err) => err);
  }, []);

  useEffect(() => {
    if (currentFilter) {
      // setFilters(_.uniqBy(selectedSlots, "category.name").filter(i => i.category.name === currentFilter).map(i => i.category.name));
      setFilteredBookedSlots(selectedSlots.filter((i) => i.category.name === currentFilter));
    } else {
      setFilteredBookedSlots(selectedSlots);
    }
    setFilters(_.uniqBy(selectedSlots, 'category.name').map((i) => i.category.name));
  }, [currentFilter, selectedSlots]);

  const onSubmit = () => {
    if (userData?._id) {
      const sessions: AddPayload[] = selectedSlots.map((slot) => ({
        userId: userData._id,
        tutorId: tutorId,
        planId: planId,
        category: slot.category,
        categoryType: categoryType,
        sessionDuration: 60,
        type: "plan",
        scheduledTime: {
          start: slot.start,
          end: slot.end
        }
      }));
      setLoading(true);
      plansApi.addSessions(userData._id, planId, sessions)
        .then(() => {
          setLoading(false);
          setBookSessionsPortal(false);
          window.location.reload();
        })
        .catch(() => {
          setLoading(false);
        });
    }
  };

  return (
    <BottomSheet>
      <div className="w-full h-full flex flex-col">
        <div className="sticky top-0 w-full flex items-center px-7 md:px-10 py-7 bg-white border-b z-40">
          <div className="w-full flex items-center">
            <h1 className="w-full mx-auto text-base md:text-xl text-center font-semibold">Schedule Your Classes</h1>
          </div>
          <MdClose className="right-0 text-2xl cursor-pointer ml-auto" onClick={() => setBookSessionsPortal(false)} />
        </div>

        <div className="w-full h-full flex flex-col md:flex-row items-stretch gap-2  overflow-y-scroll px-7 md:px-10">
          <div className="w-full md:w-3/4 max-h-[600px] flex flex-col p-2">
            <CalenderProvider>
              <Calender
                slotSelectable
                bookedSlots={classes
                  .filter((c) => c.status !== 'cancelled')
                  .map((session) => ({
                    status: session.status,
                    tutor: session.tutor,
                    type: session.type,
                    category: session.category,
                    slot: session.scheduledTime
                  }))}
                availableSlots={availableSlots}
                recurrence
                onReccurence={(currWeekSlots: CategorySlot[]) => {
                  // setSelectedSlots([...selectedSlots, ...currWeekSlots])
                  let temp: CategorySlot[] = [];
                  let weekNo = -2;
                  let maxWeekNo = Math.round(remainingSessions / currWeekSlots.length);
                  while (weekNo < maxWeekNo) {
                    weekNo += 1;
                    for (let i = 0; i < currWeekSlots.length; i++) {
                      let slot = currWeekSlots[i];
                      let slotStart = formatISO(parseJSON(addWeeks(parseJSON(slot.start), weekNo + 1)));
                      let slotEnd = formatISO(parseJSON(addWeeks(parseJSON(slot.end), weekNo + 1)));
                      // let trainerslotStart = availableSlots.find(
                      //   (item: any) =>
                      //     new Date(item.start) >= new Date(slotStart) && new Date(item.start) < new Date(item.end)
                      // );
                      // let trainerslotEnd = availableSlots.find((item: any) => new Date(item.end) <= new Date(slotEnd));
                      let trainerslotStart = availableSlots.find((item: any) => new Date(slotStart) >= new Date(item.start) && new Date(slotStart) < new Date(item.end));
                      let trainerslotEnd = availableSlots.find((item: any) => new Date(slotEnd) <= new Date(item.end) && new Date(slotEnd) > new Date(item.start));

                      if (!trainerslotStart || !trainerslotEnd) {
                        continue;
                      }
                      temp.push({
                        start: utcToZonedTime(slotStart, timezone).toISOString(),
                        end: utcToZonedTime(slotEnd, timezone).toISOString(),
                        // @ts-ignore
                        category: {
                          ...category,
                          name: slot.category.name
                        }
                      });
                    }
                  }
                  setSelectedSlots(temp);
                }}
                selectedSlots={selectedSlots}
                onSelectSlot={(slot: any, remove?: number) => {
                  if (remove) {
                    setSelectedSlots(selectedSlots.filter((s) => s.start !== slot.start && s.end !== slot.end));
                  } else {
                    if (selectedSlots.length === remainingSessions) {
                      return;
                    }
                    let s = selectedSlots.find((item: any) => new Date(slot.end) > new Date(item.start) && new Date(slot.end) < new Date(item.end));
                    let e = selectedSlots.find((item: any) => new Date(slot.start) > new Date(item.start) && new Date(slot.start) < new Date(item.end));
                    if (s || e) return;
                    if (categoryType === 'subject') {
                      setSelectedSlot(slot);
                      if (category.name.length === 1) {
                        setSelectedSlots([
                          ...selectedSlots,
                          {
                            start: slot.start,
                            end: slot.end,
                            category: {
                              ...category,
                              name: category.name[0]
                            }
                          }
                        ]);
                        setSelectedSlot(undefined);
                      } else {
                        setShowPrompt(true);
                      }
                    } else {
                      setSelectedSlots([
                        //@ts-ignore
                        ...selectedSlots,
                        {
                          start: slot.start,
                          end: slot.end,
                          //@ts-ignore
                          category: {
                            ...category,
                            name: category.name[0]
                          }
                        }
                      ]);
                    }
                  }
                }}
              />
            </CalenderProvider>

          </div>

          <div className="w-full md:w-1/4 flex flex-col gap-2">
            {/* <div className="w-full p-4 text-sm bg-neutralBlue2 text-primary">
              <span className="font-medium">Note: </span>
              Number of available days to be set as {MIN_DAYS_TO_SELECT} days.
            </div> */}

            <div className="w-full flex flex-col gap-2 px-4 py-2 text-sm bg-neutralBlue2 text-primary">
              <h3 className="text-base font-medium">
                {completedSessions}/{totalSessions} <span className="text-sm text-gray-400">Completed</span>
              </h3>
            </div>

            <div className="w-full flex flex-col gap-2 px-4 pt-2 text-sm bg-neutralBlue2 text-primary overflow-hidden">
              <h3 className="text-base font-medium">
                {selectedSlots.length} <span className="text-sm text-gray-400">Booked</span>
              </h3>
              {selectedSlots.length > 0 && (
                <p className="text-xs text-primary">
                  (If you confirm scheduling these {selectedSlots.length} sessions you will have{' '}
                  {remainingSessions - selectedSlots.length} more sessions remaining to be scheduled)
                </p>
              )}
              <div className="w-full flex gap-2 bg-neutralBlue1 pt-2 px-4">
                <h1
                  onClick={() => setCurrentFilter('')}
                  className={`text-sm text-black font-semibold px-2 py-2 ${!currentFilter && 'bg-white'
                    } rounded-t-md cursor-pointer`}
                >
                  All
                </h1>
                {filters.map((i, index) => (
                  <h1
                    key={index}
                    onClick={() => setCurrentFilter(i)}
                    className={`text-sm text-black font-semibold px-2 py-2 ${currentFilter === i && 'bg-white'
                      } rounded-t-md cursor-pointer`}
                  >
                    {i}
                  </h1>
                ))}
              </div>
              <div className="w-full max-h-[440px] flex flex-col gap-2 overflow-y-scroll">
                {filteredBookedSlots.map((slot, index) => (
                  <div
                    key={index}
                    onClick={() => setToday(startOfWeek(new Date(slot.start)))}
                    className="w-full p-4 bg-white border border-l-4 border-neutralGray3 rounded-md flex items-start gap-4 cursor-pointer"
                  >
                    <div className="flex flex-col items-start border-r pr-2">
                      <p className="text-sm font-semibold">{formatInTimeZone(parseJSON(slot.start), timezone, 'd')}</p>
                      <p className="text-xs font-semibold">
                        {formatInTimeZone(parseJSON(slot.start), timezone, 'MMM')}
                      </p>
                    </div>

                    <div className="flex flex-col items-start">
                      <p className="text-xs font-semibold">
                        {formatInTimeZone(parseJSON(slot.start), timezone, 'eeee')}
                      </p>
                      <p className="text-sm font-semibold">
                        {formatInTimeZone(parseJSON(slot.start), timezone, 'p')} -{' '}
                        {formatInTimeZone(parseJSON(slot.end), timezone, 'p')}
                      </p>
                    </div>
                    <div className="ml-auto">
                      <MdDeleteOutline className='text-base text-primary cursor-pointer' onClick={() => {
                        setSelectedSlots(selectedSlots.filter((s) => s.start !== slot.start && s.end !== slot.end))
                      }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="w-full flex items-center justify-center px-7 md:px-10 py-7 bg-white border-t">
          <div className="w-full max-w-[164px]">
            <Button loading={loading} block text="Submit" onClick={() => onSubmit()} />
          </div>
        </div>

        {showPrompt && (
          <Portal>
            <div className="fixed top-0 left-0 w-full h-full z-50">
              <div className="relative w-full h-full flex items-center justify-center">
                <div
                  onClick={() => setShowPrompt(false)}
                  className="absolute top-0 left-0 bg-black/75 w-full h-full "
                ></div>
                <div className="min-w-[300px] z-50 bg-white flex flex-col items-center justify-center p-7 rounded-md drop-shadow-md overflow-hidden">
                  <h1 className="w-max text-lg font-medium pb-4">Select subject</h1>
                  {category.name.length > 0 &&
                    category.name.map((item, index) => (
                      <p
                        key={index}
                        onClick={() => {
                          if (!selectedSlot) {
                            return;
                          }
                          setSelectedSlots([
                            ...selectedSlots,
                            {
                              start: selectedSlot.start,
                              end: selectedSlot.end,
                              category: {
                                ...category,
                                name: item
                              }
                            }
                          ]);
                          setSelectedSlot(undefined);
                          setShowPrompt(false);
                        }}
                        className="w-full py-2 px-4 text-base text-center cursor-pointer hover:bg-neutralBlue2"
                      >
                        {item}
                      </p>
                    ))}
                </div>
              </div>
            </div>
          </Portal>
        )}
      </div>
    </BottomSheet>
  );
}
